import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import {
  fetchTickets,
  fetchTicketMonthlyReport,
  getTicketCircles,
  getTicketDivisions,
  getTicketKpis,
  getTicketLocations,
  getTicketMonthlyReport,
  getTicketSections,
  getTicketSubDivisions,
  humanizeError,
} from '../services/api';
import { useSearch } from '../context/SearchContext';
import ClientProjectDropdowns from '../components/filters/ClientProjectDropdowns';
import Dropdown from '../components/global/Dropdown';
import Button from '../components/global/Button';
import { getActiveFilterMeta } from '../constants/hierarchyFilters';
import { useClientProjectScope } from '../hooks/useClientProjectScope';
import { useHierarchyFilters } from '../hooks/useHierarchyFilters';
import BarChart from '../components/charts/Barchart';
import FiltersBar from '../components/dashboard/FiltersBar';
import TableToolbar from '../components/dashboard/TableToolbar';
import CardWidget from '../features/dashboard/components/configs/widgets/CardWidget';
import exportIcon from '../assets/icons/ExportIcon.svg';
import { buildPageList } from '../utils/pagination';
import './Dashboard.css';
import './TicketDashboard.css';

const PAGE_SIZE = 6;

const STATUS_TONE = {
  Open: 'open',
  'In Progress': 'in-progress',
  Resolved: 'resolved',
  Closed: 'closed',
};

function formatRaisedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
}

export default function TicketDashboard() {
  const navigate = useNavigate();
  const { query: globalQuery } = useSearch();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [tableSearch, setTableSearch] = useState('');
  const [reportRange, setReportRange] = useState('Year');
  const [monthlyReport, setMonthlyReport] = useState(null);

  const scope = useClientProjectScope();
  const ticketHierarchyRows = useMemo(
    () =>
      tickets.map((t) => ({
        state: t.circle,
        depot: t.division,
        area: t.location,
        qtr: t.section,
      })),
    [tickets],
  );
  const hierarchy = useHierarchyFilters(ticketHierarchyRows);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchTickets();
      setTickets(data);
    } catch (err) {
      setError(humanizeError(err, 'Could not load tickets. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchTicketMonthlyReport(reportRange)
      .then((report) => {
        if (!cancelled) setMonthlyReport(report);
      })
      .catch(() => {
        if (!cancelled) setMonthlyReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reportRange]);

  const kpis = useMemo(() => getTicketKpis(), []);

  const kpiCards = useMemo(
    () => [
      {
        id: 'total',
        title: 'Total Tickets',
        value: kpis.total.toLocaleString('en-IN'),
        bottomText: 'Across all regions',
        graphData: [4, 5, 5, 6, 6, 7, 8, 9],
        graphAriaLabel: 'Total tickets trend',
      },
      {
        id: 'open',
        title: 'Open Tickets',
        value: kpis.open.toLocaleString('en-IN'),
        bottomText: 'Awaiting assignment',
        graphData: [3, 4, 4, 5, 6, 6, 7, 8],
        graphAriaLabel: 'Open tickets trend',
      },
      {
        id: 'in-progress',
        title: 'In Progress',
        value: kpis.inProgress.toLocaleString('en-IN'),
        bottomText: 'Being worked on',
        graphData: [4, 5, 6, 5, 6, 7, 7, 8],
        graphAriaLabel: 'In progress tickets trend',
      },
      {
        id: 'resolved',
        title: 'Resolved',
        value: kpis.resolved.toLocaleString('en-IN'),
        bottomText: 'Closed satisfactorily',
        graphData: [3, 4, 5, 6, 6, 7, 8, 9],
        graphAriaLabel: 'Resolved tickets trend',
      },
      {
        id: 'avg-resolution',
        title: 'Avg. Resolution',
        value: `${kpis.avgResolutionDays}d`,
        bottomText: '0.3d vs last',
        graphData: [5, 5, 6, 6, 7, 7, 8, 8],
        graphAriaLabel: 'Average resolution trend',
      },
    ],
    [kpis],
  );

  const filterOptions = useMemo(
    () => [
      {
        key: 'circle',
        label: 'Circle',
        allLabel: 'Select Circle',
        options: getTicketCircles(),
      },
      {
        key: 'division',
        label: 'Division',
        allLabel: 'Select Division',
        options: getTicketDivisions(),
      },
      {
        key: 'subDivision',
        label: 'Sub-Division',
        allLabel: 'Select Sub-Division',
        options: getTicketSubDivisions(),
      },
      {
        key: 'section',
        label: 'Section',
        allLabel: 'Select Section',
        options: getTicketSections(),
      },
      {
        key: 'location',
        label: 'Meter Location',
        allLabel: 'Select Meter Location',
        options: getTicketLocations(),
      },
    ],
    [],
  );

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const resetFilters = () => setFilters({});
  const hasFilters = Object.keys(filters).length > 0;

  const filteredTickets = useMemo(() => {
    const q = `${globalQuery || ''} ${tableSearch || ''}`.trim().toLowerCase();
    const hierarchyFieldMap = {
      state: 'circle',
      depot: 'division',
      area: 'location',
      qtr: 'section',
    };
    const hierarchyMeta = hierarchy.filterByField
      ? getActiveFilterMeta(hierarchy.filterByField)
      : null;
    const hierarchyTicketField = hierarchyMeta
      ? hierarchyFieldMap[hierarchyMeta.field]
      : null;

    return tickets.filter((t) => {
      if (filters.circle && t.circle !== filters.circle) return false;
      if (filters.division && t.division !== filters.division) return false;
      if (filters.subDivision && t.subDivision !== filters.subDivision) return false;
      if (filters.section && t.section !== filters.section) return false;
      if (filters.location && t.location !== filters.location) return false;
      if (
        hierarchyTicketField &&
        hierarchy.locationFilter &&
        t[hierarchyTicketField] !== hierarchy.locationFilter
      ) {
        return false;
      }
      if (!q) return true;
      return (
        t.id.toLowerCase().includes(q) ||
        String(t.fieldOfficer).toLowerCase().includes(q) ||
        String(t.issueType).toLowerCase().includes(q) ||
        String(t.category).toLowerCase().includes(q) ||
        String(t.location).toLowerCase().includes(q) ||
        String(t.assignedTo).toLowerCase().includes(q)
      );
    });
  }, [tickets, filters, globalQuery, tableSearch, hierarchy.filterByField, hierarchy.locationFilter]);

  useEffect(() => {
    setPage(1);
  }, [filters, globalQuery, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredTickets.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageList = useMemo(() => buildPageList(safePage, totalPages), [safePage, totalPages]);

  const reportData = useMemo(() => {
    const hasServerData =
      monthlyReport &&
      monthlyReport.range === reportRange &&
      ((monthlyReport.raised || []).some((v) => v > 0) ||
        (monthlyReport.resolved || []).some((v) => v > 0));

    const trend = hasServerData ? monthlyReport : getTicketMonthlyReport(reportRange);
    return {
      xAxisData: trend.labels,
      seriesData: [
        { name: 'Raised', data: trend.raised },
        { name: 'Resolved', data: trend.resolved },
      ],
    };
  }, [monthlyReport, reportRange]);

  const handleExport = () => {
    const headers = [
      'Ticket ID',
      'Field Officer',
      'Issue Type',
      'Category',
      'Location',
      'Raised',
      'Status',
      'Assigned To',
    ];
    const rows = filteredTickets.map((t) => [
      t.id,
      t.fieldOfficer,
      t.issueType,
      t.category,
      t.location,
      formatRaisedAt(t.raisedAt),
      t.status,
      t.assignedTo,
    ]);
    const csv = [headers, ...rows]
      .map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dash-loading">
          <div className="loading-spinner" />
          <p>Loading tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dash-error">
          <p>Failed to load tickets: {error}</p>
          <Button className="retry-btn" onClick={load}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard ticket-dashboard">
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Tickets</h2>
          <p className="dash-subtitle">Operational Overview</p>
        </div>
        <div className="dash-header-actions dashboard-header-toolbar">
          <ClientProjectDropdowns
            clientOptions={scope.clientDropdownOptions}
            projectOptions={scope.projectDropdownOptions}
            selectedClientId={scope.selectedClientId}
            selectedProjectId={scope.selectedProjectId}
            onClientChange={scope.onClientChange}
            onProjectChange={scope.onProjectChange}
            projectDisabled={scope.projectNeedsClientFirst || scope.noProjectsForClient}
            projectPlaceholder={scope.projectPlaceholder}
          />
          <Button type="button" className="dash-header-export" onClick={handleExport}>
            <img className="dash-header-export-icon" src={exportIcon} alt="" aria-hidden="true" />
            <span className="dash-header-export-text">Export</span>
          </Button>
        </div>
      </div>

      <FiltersBar
        filters={filters}
        filterOptions={filterOptions}
        onSetFilter={setFilter}
        onGetData={load}
        onReset={resetFilters}
        hasFilters={hasFilters}
      />

      <CardWidget items={kpiCards} />

      <div className="report-card tk-report-card">
        <div className="report-header">
          <h3 className="report-title">Quick Monthly Report</h3>
          <div className="report-controls">
            <Dropdown
              options={[{ value: 'All', label: 'All' }]}
              value="All"
              onChange={() => {}}
              ariaLabel="Report filter"
            />
            <div className="report-range">
              {['Month', 'Week', 'Year'].map((r) => {
                const isActive = reportRange === r;
                return (
                  <Button
                    key={r}
                    type="button"
                    variant={isActive ? 'primary' : 'tertiary'}
                    className={`report-range-btn ${isActive ? 'active' : ''}`}
                    onClick={() => setReportRange(r)}
                  >
                    {r}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        <BarChart
          data={reportData}
          defaultHeight={340}
          ariaLabel="Tickets raised vs resolved"
          seriesColors={['#0f2e6e', '#01b574']}
          timeRange={reportRange}
          showTimeRangeInTooltip={false}
        />
      </div>

      <section className="tk-table-card">
        <TableToolbar
          searchValue={tableSearch}
          onSearchChange={setTableSearch}
          hierarchy={hierarchy}
        />

        <div className="tk-table-scroll">
          <table className="tk-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Field Officer</th>
                <th>Issue Type</th>
                <th>Category</th>
                <th>Location</th>
                <th>Raised</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="tk-empty">
                    No tickets match the current filters.
                  </td>
                </tr>
              ) : (
                pageRows.map((t) => (
                  <tr key={t.id}>
                    <td className="tk-id-cell">{t.id}</td>
                    <td>{t.fieldOfficer}</td>
                    <td>{t.issueType}</td>
                    <td>{t.category}</td>
                    <td>{t.location}</td>
                    <td className="tk-raised-cell">{formatRaisedAt(t.raisedAt)}</td>
                    <td>
                      <span className={`tk-status-pill ${STATUS_TONE[t.status] || 'open'}`}>
                        <span className="tk-status-dot" aria-hidden="true" />
                        {t.status}
                      </span>
                    </td>
                    <td>{t.assignedTo}</td>
                    <td>
                      <button
                        type="button"
                        className="tk-view-more"
                        onClick={() => navigate(`/tickets/${t.id}`)}
                      >
                        View More <FiEye />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="tk-pagination">
          <span className="tk-total-count" aria-live="polite">
            Total: {filteredTickets.length}
          </span>
          <div className="tk-pagination-controls">
            <button
              type="button"
              className="tk-page-arrow"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <FiChevronLeft />
            </button>
            {pageList.map((entry, idx) => {
              if (entry === 'left-ellipsis' || entry === 'right-ellipsis') {
                return (
                  <span key={`${entry}-${idx}`} className="tk-page-ellipsis">
                    …
                  </span>
                );
              }
              const isActive = entry === safePage;
              return (
                <button
                  key={entry}
                  type="button"
                  className={`tk-page-num ${isActive ? 'active' : ''}`}
                  onClick={() => setPage(entry)}
                >
                  {entry}
                </button>
              );
            })}
            <button
              type="button"
              className="tk-page-arrow"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
